// Agents (AgentOps) console navigation model — single source of truth shared
// by the console page (AgentRunsList) and the TopNav breadcrumb, so the
// console navigates through the breadcrumb exactly like the Admin Console
// (Home › Agents › Group ▾ › Surface ▾). Kept dependency-free so the eagerly
// loaded breadcrumb can import it without pulling in the heavy console page.

export const AGENT_PERSONA_TABS: Record<string, { label: string; description: string; tabs: string[] }> = {
  operator: { label: "Operator", description: "Runs the fleet day-to-day — runs, queue, incidents.",
    tabs: ["overview", "runs", "queue", "recovery", "incidents", "alerts", "tools", "agentmap"] },
  builder: { label: "Builder / Developer", description: "Designs & ships agents — config, tools, tests, automation.",
    tabs: ["overview", "registry", "sandbox", "capabilities", "conversations", "diff", "templates", "triggers", "evals"] },
  governance: { label: "Governance / Risk", description: "Owns safety, access & compliance.",
    tabs: ["overview", "policies", "access", "approvals", "guardrails", "dryruns", "credentials", "environments", "agentmap"] },
  executive: { label: "Executive", description: "Fleet health, cost & quality at a glance.",
    tabs: ["overview", "scorecard", "costusage", "sla", "incidents"] },
}

// Display label per console surface (single source for the breadcrumb + page).
export const AGENT_VIEW_LABEL: Record<string, string> = {
  overview: "Overview",
  runs: "Runs", tools: "Tool Calls", conversations: "Conversations", artifacts: "Artifacts",
  dryruns: "Dry Runs", diff: "Diff", approvals: "Approvals", provenance: "Provenance",
  triggers: "Triggers", topology: "Topology", knowledge: "Knowledge", alerts: "Alerts",
  guardrails: "Guardrails", sandbox: "Sandbox", incidents: "Incidents", scorecard: "Scorecard",
  recommendations: "Recommendations", dependencies: "Dependencies", policies: "Policies",
  access: "Access", recovery: "Recovery", queue: "Queue", evals: "Evals", feedback: "Feedback",
  digests: "Digests", cost: "Cost", registry: "Registry", environments: "Environments",
  templates: "Templates", catalog: "Tool Catalog", credentials: "Credentials", quotas: "Quotas",
  sla: "SLAs", usage: "Usage", agentmap: "Agent Map", costusage: "Cost & Usage",
  capabilities: "Capabilities", personas: "Personas",
}

// Grouped console IA — collapses the flat surfaces into role-aligned groups.
// `slug` is the URL segment so paths read /agents/<group>/<surface>, matching
// the Admin console's /admin/<section>/<subtab>. The breadcrumb renders the
// active group + surface as sibling dropdowns.
export const AGENT_CONSOLE_GROUPS: { label: string; slug: string; views: string[] }[] = [
  // Fleet-health landing.
  { label: "Overview", slug: "overview", views: ["overview"] },
  // Definitions — what an agent IS / can do (configuration & capabilities).
  // The cross-cutting cut is Definitions vs Activity, not by entity: registry,
  // templates and environments (agent config), Capabilities (tool catalog +
  // knowledge sources), triggers (when it fires) and the agent map (how agents
  // relate) all describe the configured system, not its history.
  { label: "Definitions", slug: "definitions", views: ["registry", "templates", "capabilities", "environments", "triggers", "agentmap"] },
  // Activity / History — what agents DID. Per-run facets (artifacts,
  // provenance, feedback) live inside the Run detail, not as peer surfaces.
  { label: "Activity", slug: "activity", views: ["runs", "conversations", "tools", "diff"] },
  // Live orchestration + health of in-flight work.
  { label: "Operations", slug: "operations", views: ["queue", "recovery", "incidents", "alerts", "sla"] },
  { label: "Governance & Security", slug: "governance", views: ["policies", "access", "guardrails", "approvals", "credentials"] },
  { label: "Cost & Budgeting", slug: "cost", views: ["costusage", "quotas"] },
  // Pre-production testing.
  { label: "Testing", slug: "testing", views: ["dryruns", "sandbox", "evals"] },
  // Post-production quality / monitoring — distinct surfaces, not stacked.
  { label: "Quality", slug: "quality", views: ["scorecard", "recommendations", "digests", "feedback"] },
]

/** The group that owns a given surface (falls back to the first group). */
export function agentGroupForView(view: string) {
  return AGENT_CONSOLE_GROUPS.find((g) => g.views.includes(view)) ?? AGENT_CONSOLE_GROUPS[0]
}

/** Canonical path for a surface: /agents/<group>/<view>, mirroring the Admin
 *  console's /admin/<section>/<subtab>. Overview and Personas are single-segment
 *  specials (the landing + the persona manager). Persona rides as a query lens. */
export function agentPathForView(view: string, persona = ""): string {
  const q = persona ? `?persona=${persona}` : ""
  if (view === "overview" || view === "personas") return `/agents/${view}${q}`
  return `/agents/${agentGroupForView(view).slug}/${view}${q}`
}

/** Surfaces shown for a group, optionally narrowed by persona (current view
 *  always kept so the breadcrumb never hides where you are). */
export function agentSurfacesForGroup(group: { views: string[] }, persona: string, currentView: string) {
  return group.views.filter(
    (v) => !persona || AGENT_PERSONA_TABS[persona]?.tabs.includes(v) || v === currentView)
}
